import React, { useEffect, useRef, useState } from "react";
import { Plus, Minus } from "lucide-react";
import { Button } from "@/shadcn-components/ui/button";
import { Input } from "@/shadcn-components/ui/input";
import { useControlState } from "../hooks/useControlState";
import { useAccountInformationStore } from "@/vybe-trader-client";
import {
  slTpPriceFromMoney,
  type SlTpSide,
  type SlTpSymbolInfo,
} from "../lib/slTpMoney";

interface TradeMarketExecutionContentProps {
  bidPrice: number;
  askPrice: number;
  formatPrice: (price: number) => string;
  stopLoss: number | null;
  stopLossInput: string;
  takeProfit: number | null;
  takeProfitInput: string;
  onStopLossChange: (change: number) => void;
  onStopLossInputChange: (value: string) => void;
  onTakeProfitChange: (change: number) => void;
  onTakeProfitInputChange: (value: string) => void;
  onSellByMarket: () => void;
  onBuyByMarket: () => void;
  isTrading: boolean;
  isDesktop?: boolean;
  /** Current lot size — needed for money⇄price SL/TP conversion. */
  volume?: number;
  /** Price digits for spread display. */
  digits?: number;
  /** Tick-value info for money⇄price SL/TP conversion. */
  symbolInfo?: SlTpSymbolInfo | null;
}

type SlTpUnit = "px" | "money";

const MONEY_STEP = 10;

export default function TradeMarketExecutionContent({
  bidPrice,
  askPrice,
  formatPrice,
  stopLoss,
  stopLossInput,
  takeProfit,
  takeProfitInput,
  onStopLossChange,
  onStopLossInputChange,
  onTakeProfitChange,
  onTakeProfitInputChange,
  onSellByMarket,
  onBuyByMarket,
  isTrading,
  isDesktop = false,
  volume = 0.01,
  digits = 5,
  symbolInfo = null,
}: TradeMarketExecutionContentProps) {
  // Shared control-state model (proposal 10). In-flight while trading;
  // disabled when there is no price. The guard swallows double-fires.
  const sellControl = useControlState({ isTrading, isDisabled: !bidPrice });
  const buyControl = useControlState({ isTrading, isDisabled: !askPrice });

  // Account currency for the money unit toggle; USD fallback pre-login.
  const accountCurrency =
    useAccountInformationStore((state) => state.account?.currency) || "USD";

  // px ⇄ money unit toggle per field. In money mode the typed money figure is
  // authoritative; the derived price is re-pushed to the parent whenever
  // volume or the quote moves. The parent / hub always receives a plain price.
  const [slUnit, setSlUnit] = useState<SlTpUnit>("px");
  const [tpUnit, setTpUnit] = useState<SlTpUnit>("px");
  const [slMoney, setSlMoney] = useState("");
  const [tpMoney, setTpMoney] = useState("");

  const slPushedRef = useRef("");
  const tpPushedRef = useRef("");

  const derivePrice = (
    moneyStr: string,
    side: SlTpSide
  ): number | null => {
    const money = parseFloat(moneyStr);
    if (!symbolInfo || isNaN(money) || money <= 0) return null;
    const base = side === "sl" ? bidPrice : askPrice;
    return slTpPriceFromMoney(money, side, "buy", base, volume, symbolInfo);
  };

  const slDerived = slUnit === "money" ? derivePrice(slMoney, "sl") : null;
  const tpDerived = tpUnit === "money" ? derivePrice(tpMoney, "tp") : null;

  // Keep the submitted price in sync while money mode is active — the money
  // figure stays authoritative as volume/quote move.
  useEffect(() => {
    if (slUnit !== "money") return;
    const next = slDerived !== null ? slDerived.toFixed(digits) : "";
    if (next && next !== slPushedRef.current) {
      slPushedRef.current = next;
      onStopLossInputChange(next);
    }
  }, [slUnit, slDerived, digits, onStopLossInputChange]);

  useEffect(() => {
    if (tpUnit !== "money") return;
    const next = tpDerived !== null ? tpDerived.toFixed(digits) : "";
    if (next && next !== tpPushedRef.current) {
      tpPushedRef.current = next;
      onTakeProfitInputChange(next);
    }
  }, [tpUnit, tpDerived, digits, onTakeProfitInputChange]);

  // Helper to set default stop loss based on current bid price
  const handleStopLossClick = () => {
    if (slUnit === "px" && !stopLoss && bidPrice > 0) {
      const defaultStopLoss = bidPrice * 0.99; // 1% below bid
      onStopLossInputChange(defaultStopLoss.toString());
    }
  };

  // Helper to set default take profit based on current ask price
  const handleTakeProfitClick = () => {
    if (tpUnit === "px" && !takeProfit && askPrice > 0) {
      const defaultTakeProfit = askPrice * 1.01; // 1% above ask
      onTakeProfitInputChange(defaultTakeProfit.toString());
    }
  };

  const stepMoney = (
    current: string,
    delta: number,
    set: (v: string) => void
  ) => {
    const parsed = parseFloat(current);
    const next = Math.max(0, (isNaN(parsed) ? 0 : parsed) + delta);
    set(next > 0 ? next.toFixed(2) : "");
  };

  const spreadPts =
    bidPrice > 0 && askPrice > 0
      ? Math.round((askPrice - bidPrice) * Math.pow(10, digits))
      : null;

  const renderRow = (
    label: string,
    unit: SlTpUnit,
    setUnit: (u: SlTpUnit) => void,
    moneyValue: string,
    setMoneyValue: (v: string) => void,
    priceInput: string,
    onPriceStep: (change: number) => void,
    onPriceInput: (value: string) => void,
    onInputClick: () => void,
    currentPrice: number
  ) => (
    <div className="flex h-[38px] items-center gap-2 border-b border-border/60 pl-3 pr-2.5 last:border-b-0">
      <span className="w-[72px] flex-none text-xs font-medium text-foreground">
        {label}
      </span>
      <Button
        variant="outline"
        size="sm"
        onClick={() =>
          unit === "money"
            ? stepMoney(moneyValue, -MONEY_STEP, setMoneyValue)
            : onPriceStep(-0.0001)
        }
        className="h-[26px] w-7 flex-none border-border p-0 text-muted-foreground hover:text-foreground"
        aria-label={`Decrease ${label}`}
      >
        <Minus size={12} />
      </Button>
      <Input
        type="number"
        value={unit === "money" ? moneyValue : priceInput}
        onChange={(e) =>
          unit === "money"
            ? setMoneyValue(e.target.value)
            : onPriceInput(e.target.value)
        }
        onClick={onInputClick}
        placeholder="Not set"
        className="h-[26px] min-w-0 flex-1 border-border px-1 text-center font-mono text-[11px] tabular-nums placeholder:text-muted-foreground/60"
        step={unit === "money" ? "1" : "0.0001"}
      />
      <Button
        variant="outline"
        size="sm"
        onClick={() =>
          unit === "money"
            ? stepMoney(moneyValue, MONEY_STEP, setMoneyValue)
            : onPriceStep(0.0001)
        }
        className="h-[26px] w-7 flex-none border-border p-0 text-muted-foreground hover:text-foreground"
        aria-label={`Increase ${label}`}
      >
        <Plus size={12} />
      </Button>
      <div className="flex flex-none overflow-hidden rounded-md border border-border">
        <button
          type="button"
          onClick={() => setUnit("px")}
          className={`px-1.5 py-0.5 font-mono text-[9.5px] ${
            unit === "px"
              ? "bg-muted text-foreground"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          px
        </button>
        <button
          type="button"
          onClick={() => setUnit("money")}
          className={`px-1.5 py-0.5 font-mono text-[9.5px] ${
            unit === "money"
              ? "bg-muted text-foreground"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          {accountCurrency}
        </button>
      </div>
      <span className="w-14 flex-none text-right font-mono text-[10.5px] tabular-nums text-muted-foreground">
        {formatPrice(currentPrice)}
      </span>
    </div>
  );

  return (
    <>
      {/* Stop Loss / Take Profit group */}
      <div className="overflow-hidden rounded-lg border border-border">
        {renderRow(
          "Stop Loss",
          slUnit,
          setSlUnit,
          slMoney,
          setSlMoney,
          stopLossInput,
          onStopLossChange,
          onStopLossInputChange,
          handleStopLossClick,
          bidPrice
        )}
        {renderRow(
          "Take Profit",
          tpUnit,
          setTpUnit,
          tpMoney,
          setTpMoney,
          takeProfitInput,
          onTakeProfitChange,
          onTakeProfitInputChange,
          handleTakeProfitClick,
          askPrice
        )}
      </div>

      {/* Converted preview while a money-denominated level is active */}
      {(slUnit === "money" && slDerived !== null) ||
      (tpUnit === "money" && tpDerived !== null) ? (
        <div className="px-1 text-right font-mono text-[10px] text-muted-foreground">
          {slUnit === "money" && slDerived !== null && (
            <div>
              SL −{parseFloat(slMoney).toFixed(2)} {accountCurrency} ⇒{" "}
              <span className="text-foreground">{formatPrice(slDerived)}</span>{" "}
              @ {volume.toFixed(2)} lot
            </div>
          )}
          {tpUnit === "money" && tpDerived !== null && (
            <div>
              TP +{parseFloat(tpMoney).toFixed(2)} {accountCurrency} ⇒{" "}
              <span className="text-foreground">{formatPrice(tpDerived)}</span>{" "}
              @ {volume.toFixed(2)} lot
            </div>
          )}
        </div>
      ) : null}

      {/* Sticky bottom action bar — spread pill + solid SELL/BUY */}
      <div
        className={
          isDesktop
            ? "border-t border-border bg-background pt-2"
            : "sticky bottom-0 z-10 -mx-4 -mb-4 border-t border-border bg-background px-4 pt-2 pb-[calc(env(safe-area-inset-bottom)+12px)]"
        }
      >
        <div className="mb-2 flex justify-center">
          <span className="rounded-full border border-border px-2.5 py-0.5 font-mono text-[10px] tabular-nums text-muted-foreground">
            spread {spreadPts !== null ? spreadPts : "—"} pts
          </span>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={sellControl.guard(onSellByMarket)}
            className={`h-[52px] flex-1 flex-col gap-0.5 rounded-lg bg-red-600 text-white hover:bg-red-700 ${sellControl.className}`}
            disabled={!bidPrice || isTrading}
            {...sellControl.ariaAttrs}
          >
            <span className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.12em] opacity-85">
              {sellControl.isInFlight && (
                <span className="vybe-control-spin" aria-hidden="true">
                  ↻
                </span>
              )}
              {isTrading ? "SELLING…" : "SELL"}
            </span>
            <span className="font-mono text-base font-semibold tabular-nums">
              {formatPrice(bidPrice)}
            </span>
          </Button>
          <Button
            onClick={buyControl.guard(onBuyByMarket)}
            className={`h-[52px] flex-1 flex-col gap-0.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 ${buyControl.className}`}
            disabled={!askPrice || isTrading}
            {...buyControl.ariaAttrs}
          >
            <span className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.12em] opacity-85">
              {buyControl.isInFlight && (
                <span className="vybe-control-spin" aria-hidden="true">
                  ↻
                </span>
              )}
              {isTrading ? "BUYING…" : "BUY"}
            </span>
            <span className="font-mono text-base font-semibold tabular-nums">
              {formatPrice(askPrice)}
            </span>
          </Button>
        </div>
      </div>
    </>
  );
}
